import React, { useState, useEffect } from 'react';
import HeroSection from './components/HeroSection';
import AnimatedDiscoverSection from './components/AnimatedDiscoverSection';
import PostToTopicAnimation from './components/PostToTopicAnimation';
import FAQSection from './components/FAQSection';
import AnimatedPost from './components/AnimatedPost';
import WaitlistForm from './components/WaitlistForm';

const LynkrWebsite = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Myriad Pro, Myriad, sans-serif' }}>
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-6 md:gap-12">
            <div onClick={() => scrollToSection('home')} className="flex items-center gap-2 cursor-pointer">
              <img src="/lynkr-logo.png" alt="Lynkr Logo" className="w-8 h-8 md:w-10 md:h-10" />

            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className={`${scrolled ? 'text-gray-600 hover:text-blue-500' : 'text-white'} transition-colors hover:cursor-pointer`}>Home</button>
              <button onClick={() => scrollToSection('product')} className={`${scrolled ? 'text-gray-600 hover:text-blue-500' : 'text-white'} transition-colors hover:cursor-pointer`}>Product</button>
              <button onClick={() => scrollToSection('faq')} className={`${scrolled ? 'text-gray-600 hover:text-blue-500' : 'text-white'} transition-colors hover:cursor-pointer`}>FAQ</button>
            </div>
          </div>
          <button onClick={() => scrollToSection('waitlist')} className={`${scrolled ? 'bg-black text-white hover:bg-gray-800' : 'bg-gradient-to-r from-[#4B91DE] to-[#95C7F6] text-white hover:opacity-90'} px-4 md:px-6 py-2 md:py-2.5 rounded-xl text-sm md:text-base transition-all transform hover:scale-105 cursor-pointer font-bold`}>
            Join Waitlist
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <HeroSection />

      {/* Demo Video Section */}
      <AnimatedDiscoverSection />

      {/* Features Section */}
      <section className="py-12 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6 md:gap-12">
            {/* Feature 1 */}
            <div className="bg-gray-50 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg hover:shadow-2xl transition-shadow">
              <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                Daily Trend Analysis
              </h3>
              <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8">
                Lynkr analyzes the best-performing posts in the feed and extracts the trending topics and content formats, making sure you're never missing out on a beat.
              </p>
              <PostToTopicAnimation />
            </div>

            {/* Feature 2 */}
            <div className="bg-gray-50 rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg hover:shadow-2xl transition-shadow">
              <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                Publish High-Performing Posts
              </h3>
              <p className="text-sm md:text-lg text-gray-600 mb-6 md:mb-8">
                From its analysis, Lynkr generates personalized post drafts tailored to your industry and audience, helping you maximize engagement with ease.
              </p>
              <AnimatedPost />
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Form Section */}
      <section id="waitlist" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-center text-3xl md:text-5xl font-bold mb-6">Join Our Waitlist</h2>
          <p className="text-center text-base md:text-lg text-gray-600 mb-8">
            Be the first to know when Lynkr launches and get early access to the product.
          </p>
          <WaitlistForm />
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-12 md:pt-16 pb-6 md:pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-3">Lynkr</h3>
            </div>
            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Quick Links</h4>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('home')} className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm md:text-base">Home</button>
                <button onClick={() => scrollToSection('product')} className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm md:text-base">Product</button>
                <button onClick={() => scrollToSection('faq')} className="block text-gray-400 hover:text-white transition-colors cursor-pointer text-sm md:text-base">FAQ</button>
              </div>
            </div>
            <div>
              <h4 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Contact</h4>
              <p className="text-gray-400 text-sm md:text-base">support@lynkr.io</p>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 md:mt-12 pt-6 md:pt-8 text-center text-gray-400">
            <p className="text-sm md:text-base">© 2025 Lynkr. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LynkrWebsite;